import { AppModule } from './ModuleManager';
import report, { ReportEvent } from '@/lib/report';
import useAuthStore from '@/store/useAuthStore';

/**
 * 自动刷新模块
 * 在指定时间间隔后自动刷新页面，保持应用状态最新
 */
export class AutoRefreshModule implements AppModule {
  name = 'AutoRefresh';
  priority = 4; // 中等偏低优先级

  private intervalRef: NodeJS.Timeout | null = null;
  private checkIntervalRef: NodeJS.Timeout | null = null;
  private readonly interval: number = 5 * 60 * 60 * 1000; // 5 hours
  private readonly STORAGE_KEY = 'app_last_refresh_time';
  private readonly NEXT_REFRESH_KEY = 'app_next_refresh_time';

  // Event handlers (stored as class properties for cleanup)
  private visibilityChangeHandler: (() => void) | null = null;
  private focusHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private storageChangeHandler: ((e: StorageEvent) => void) | null = null;

  init(): void {
    if (typeof window === 'undefined') return;

    console.log('[AutoRefreshModule] Initializing...');

    // 页面加载时立即检查是否需要刷新
    this.initialCheck();

    // 设置主定时器
    this.setupMainTimer();

    // 设置检查定时器（每30秒检查一次）
    this.checkIntervalRef = setInterval(() => {
      this.checkIfRefreshNeeded('periodic_check');
    }, 30 * 1000);

    // 设置事件监听器
    this.setupEventListeners();

    const currentLastRefresh = this.getLastRefreshTime();
    const nextRefreshTime = currentLastRefresh + this.interval;
    console.log('[AutoRefreshModule] Initialized');
    console.log('[AutoRefreshModule] Last refresh:', new Date(currentLastRefresh).toLocaleString());
    console.log('[AutoRefreshModule] Next refresh:', new Date(nextRefreshTime).toLocaleString());
    console.log('[AutoRefreshModule] Time until refresh:', Math.floor((nextRefreshTime - Date.now()) / 1000 / 60), 'minutes');
  }

  destroy(): void {
    if (typeof window === 'undefined') return;

    // 清理所有定时器
    if (this.intervalRef) {
      clearTimeout(this.intervalRef);
      this.intervalRef = null;
    }
    if (this.checkIntervalRef) {
      clearInterval(this.checkIntervalRef);
      this.checkIntervalRef = null;
    }

    // 移除所有事件监听器
    this.removeEventListeners();

    console.log('[AutoRefreshModule] Destroyed');
  }

  private getLastRefreshTime(): number {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  }

  private saveRefreshTime(): number {
    const now = Date.now();
    localStorage.setItem(this.STORAGE_KEY, now.toString());
    localStorage.setItem(this.NEXT_REFRESH_KEY, (now + this.interval).toString());
    return now;
  }

  private performRefresh(reason: string): void {
    const lastRefreshTime = this.getLastRefreshTime();
    console.log(`[AutoRefreshModule] Auto refreshing - reason: ${reason}`);

    report(ReportEvent.AUTO_REFRESH, {
      time: Date.now() - lastRefreshTime,
      reason,
      user_id: useAuthStore.getState().user?.id,
    });

    // 在自动刷新前设置标记，以区分自动刷新和用户主动刷新
    sessionStorage.setItem('auto_refresh_triggered', 'true');

    // 在刷新前更新本地存储
    this.saveRefreshTime();
    window.location.reload();
  }

  private checkIfRefreshNeeded(source: string = 'timer'): boolean {
    const now = Date.now();
    const lastRefreshTime = this.getLastRefreshTime();
    const timeSinceLastRefresh = now - lastRefreshTime;

    // 如果距离上次刷新已经超过设定时间，立即刷新
    if (timeSinceLastRefresh >= this.interval) {
      this.performRefresh(`${source}_interval_exceeded`);
      return true;
    }

    return false;
  }

  private detectManualRefresh(): boolean {
    // 使用 sessionStorage 标记来检测页面是否刚被自动刷新
    const wasAutoRefresh = sessionStorage.getItem('auto_refresh_triggered');

    if (wasAutoRefresh) {
      // 如果是自动刷新触发的，清除标记并跳过更新时间
      sessionStorage.removeItem('auto_refresh_triggered');
      console.log('[AutoRefreshModule] Page loaded from auto refresh');
      return false;
    }

    // 检查是否是页面刷新（而非首次加载）
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const isReload = navigation?.type === 'reload';

    // 检查是否有存储的刷新时间
    const hasStoredTime = localStorage.getItem(this.STORAGE_KEY) !== null;

    // 如果是刷新操作且之前有存储时间，说明是用户主动刷新
    if (isReload && hasStoredTime) {
      console.log('[AutoRefreshModule] User manual refresh detected, resetting timer');
      this.saveRefreshTime();
      report(ReportEvent.MANUAL_REFRESH, {
        user_id: useAuthStore.getState().user?.id,
      });
      return true;
    }

    return false;
  }

  private initialCheck(): void {
    // 首先检测是否是用户主动刷新
    const isManualRefresh = this.detectManualRefresh();

    const lastRefreshTime = this.getLastRefreshTime();
    const storedNextRefresh = localStorage.getItem(this.NEXT_REFRESH_KEY);

    // 如果是首次访问，初始化存储
    if (!localStorage.getItem(this.STORAGE_KEY)) {
      this.saveRefreshTime();
      console.log('[AutoRefreshModule] First visit, initialized refresh tracking');
    } else if (!isManualRefresh) {
      // 只有非用户主动刷新时才检查是否需要自动刷新
      // 检查是否已经超过了计划的刷新时间
      if (storedNextRefresh && Date.now() >= parseInt(storedNextRefresh, 10)) {
        console.log('[AutoRefreshModule] Scheduled refresh time has passed, refreshing now');
        // 在自动刷新前设置标记
        sessionStorage.setItem('auto_refresh_triggered', 'true');
        this.performRefresh('initial_check_overdue');
        return;
      }

      // 正常检查
      this.checkIfRefreshNeeded('initial_load');
    }
  }

  private setupMainTimer(): void {
    const lastRefreshTime = this.getLastRefreshTime();
    const timeUntilNextRefresh = Math.max(0, (lastRefreshTime + this.interval) - Date.now());

    if (timeUntilNextRefresh > 0) {
      console.log('[AutoRefreshModule] Setting main timer for', Math.floor(timeUntilNextRefresh / 1000 / 60), 'minutes');
      this.intervalRef = setTimeout(() => {
        this.performRefresh('scheduled_interval');
      }, timeUntilNextRefresh);
    }
  }

  private setupEventListeners(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // 监听页面可见性变化（处理 tab 切换）
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AutoRefreshModule] Page became visible');
        this.checkIfRefreshNeeded('visibility_change');
      }
    };

    // 监听页面激活（处理窗口焦点）
    this.focusHandler = () => {
      console.log('[AutoRefreshModule] Window received focus');
      this.checkIfRefreshNeeded('window_focus');
    };

    // 监听网络恢复
    this.onlineHandler = () => {
      console.log('[AutoRefreshModule] Network connection restored');
      setTimeout(() => {
        this.checkIfRefreshNeeded('network_restore');
      }, 1000);
    };

    // 监听存储变化（处理多标签页同步）
    this.storageChangeHandler = (e: StorageEvent) => {
      if (e.key === this.STORAGE_KEY && e.newValue) {
        const newRefreshTime = parseInt(e.newValue, 10);
        console.log('[AutoRefreshModule] Another tab performed refresh at:', new Date(newRefreshTime).toLocaleString());

        // 重新设置定时器基于新的刷新时间
        if (this.intervalRef) {
          clearTimeout(this.intervalRef);
        }
        this.setupMainTimer();
      }
    };

    // 注册事件监听器
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('storage', this.storageChangeHandler);
  }

  private removeEventListeners(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.storageChangeHandler) {
      window.removeEventListener('storage', this.storageChangeHandler);
      this.storageChangeHandler = null;
    }
  }
}
